import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Grid, message, Modal } from 'antd';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { omit, pick } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import getApolloErrors from '../../../../server/utils/getApolloErrors';
import * as permissionKind from '../../../../shared/permissions';
import { type PromoCodeDataFragment } from '../../../api/fragments/PromoCodeData';
import {
    DeletePromoCodeDocument,
    type DeletePromoCodeMutation,
    type DeletePromoCodeMutationVariables,
} from '../../../api/mutations/deletePromoCode';
import {
    UpdatePromoCodeDocument,
    type UpdatePromoCodeMutation,
    type UpdatePromoCodeMutationVariables,
} from '../../../api/mutations/updatePromoCode';
import { useCompanyContext } from '../../../components/contexts/CompanyContextManager';
import Form from '../../../components/fields/Form';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import useHandleError from '../../../utilities/useHandleError';
import { EditPromoCodeForm, type PromoCodeFormValues, usePromoCodeFormValidator } from './PromoCodeForm';

export type PromoCodeDetailsInnerPageProps = { promoCode: PromoCodeDataFragment };

const PromoCodeDetailsInnerPage = ({ promoCode }: PromoCodeDetailsInnerPageProps) => {
    const { t } = useTranslation(['promoCodeDetails']);

    const promoCodeId = promoCode.id;

    const apolloClient = useApolloClient();
    const screens = Grid.useBreakpoint();

    const { timeZonesByModuleId } = useCompanyContext();
    const timeZone = timeZonesByModuleId[promoCode.moduleId];

    const { updated, offset } = useFormattedSimpleVersioning({ versioning: promoCode.versioning, timeZone });

    const navigate = useNavigate();
    const onSubmit = useHandleError<PromoCodeFormValues>(
        async ({ moduleVariantsMapping, variantModuleIds, ...values }) => {
            const newPeriod = {
                start: dayjs(values.period.start)
                    .set('hour', values.startTime.hour())
                    .set('minute', values.startTime.minute())
                    .set('second', 0)
                    .tz(timeZone, true)
                    .toDate(),
                end: dayjs(values.period.end)
                    .set('hour', values.endTime.hour())
                    .set('minute', values.endTime.minute())
                    .set('second', 0)
                    .tz(timeZone, true)
                    .toDate(),
            };

            // submitting message
            message.loading({
                content: t('promoCodeDetails:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            const newValues = omit(['startTime', 'endTime'], values);
            // submit update
            await apolloClient
                .mutate<UpdatePromoCodeMutation, UpdatePromoCodeMutationVariables>({
                    mutation: UpdatePromoCodeDocument,
                    variables: {
                        id: promoCodeId,
                        settings: {
                            ...newValues,
                            moduleVariants: (variantModuleIds || []).map(id => ({
                                moduleId: id,
                                addedVariantSuiteIds: moduleVariantsMapping[id] || [],
                            })),
                            period: newPeriod,
                        },
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('promoCodeDetails:messages.updateSuccessful'),
                key: 'primary',
            });

            // then go back to list
            navigate('/admin/promoCodes');
        },
        [apolloClient, navigate, timeZone, promoCodeId, t]
    );

    const deletePromoCode = useCallback(() => {
        Modal.confirm({
            title: t('promoCodeDetails:deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('promoCodeDetails:deleteModal.content'),
            okText: t('promoCodeDetails:deleteModal.okText'),
            okType: 'danger',
            cancelText: t('promoCodeDetails:deleteModal.cancelText'),
            async onOk() {
                try {
                    // loading message
                    message.loading({
                        content: t('promoCodeDetails:messages.deleteSubmitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    // delete with the API
                    await apolloClient
                        .mutate<DeletePromoCodeMutation, DeletePromoCodeMutationVariables>({
                            mutation: DeletePromoCodeDocument,
                            variables: { id: promoCodeId },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    // show success
                    message.success({
                        content: t('promoCodeDetails:messages.deleteSuccessful'),
                        key: 'primary',
                    });

                    // then go back to list
                    navigate('/admin/promoCodes');
                } catch (error) {
                    const apolloErrors = getApolloErrors(error);

                    if (apolloErrors?.$root) {
                        message.error(apolloErrors?.$root);
                    }
                }
            },
        });
    }, [apolloClient, promoCodeId, navigate, t]);

    const initialValues = useMemo((): PromoCodeFormValues => {
        const { moduleVariants } = promoCode;

        return {
            ...pick(
                [
                    'promoCode',
                    'period',
                    'dealerIds',
                    'initialQuantity',
                    'isActive',
                    'trackUsage',
                    'applicationModuleIds',
                ],
                promoCode
            ),
            promoType: {
                type: promoCode.promoType.type,
                discountPromoType:
                    promoCode.promoType.__typename === 'DiscountPromoType'
                        ? pick(['amount', 'deductFromFinancing', 'description', 'discountType'], promoCode.promoType)
                        : null,
                giftPromoType:
                    promoCode.promoType.__typename === 'GiftPromoType'
                        ? pick(['description'], promoCode.promoType)
                        : null,
            },
            variantModuleIds: moduleVariants.map(({ moduleId }) => moduleId),
            moduleVariantsMapping: Object.fromEntries(
                moduleVariants.map(({ moduleId, addedVariantSuiteIds }) => [moduleId, addedVariantSuiteIds])
            ),
            startTime: dayjs(promoCode.period.start).tz(timeZone),
            endTime: dayjs(promoCode.period.end).tz(timeZone),
        };
    }, [promoCode, timeZone]);

    const footer = useMemo(() => {
        if (!hasPermissions(promoCode.permissions, [permissionKind.updatePromoCode])) {
            return null;
        }

        return [
            <Button key="submit" form="promoCodeForm" htmlType="submit" type="primary">
                {t('promoCodeDetails:actions.update')}
            </Button>,
        ];
    }, [promoCode.permissions, t]);

    const validate = usePromoCodeFormValidator();

    return (
        <ConsolePageWithHeader
            extra={
                promoCode &&
                hasPermissions(promoCode.permissions, [permissionKind.deletePromoCode]) && (
                    <Button onClick={deletePromoCode} danger>
                        {t('promoCodeDetails:actions.delete')}
                    </Button>
                )
            }
            footer={footer}
            onBack={() => navigate('/admin/promoCodes')}
            title={
                screens.md ? t('promoCodeDetails:title', { name: initialValues.promoCode }) : initialValues.promoCode
            }
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form id="promoCodeForm" name="promoCodeForm" onSubmitCapture={handleSubmit}>
                        <EditPromoCodeForm offset={offset} promoCode={promoCode} updated={updated} />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default PromoCodeDetailsInnerPage;
